import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import Grid from '@mui/material/Grid'
import TextInput from '../TextInput'
import {makeStyles} from '@mui/styles'
import {
  PERMISSION_GROUP_NOT_RESTRICTED,
  PERMISSION_GROUP_SUPER_ADMIN,
  PERMISSION_GROUP_VENDOR,
  PERMISSION_GROUP_GLOBAL_ADMIN,
} from '../../constants/ModelConstants'
import useFormChanged, {NonEmptyStringValidator, EmailValidator, NonEmptyValidator, NonEmptyArrayValidator} from '../hooks/useFormChanged'
import {Typography} from '@mui/material'
import Constant from '../Settings/SettingsConstants'
import RXRButton from '../RXRButton'
import {Colors, Typography as TypographyStyle} from '../../assets/styles'
import SelectStaffUserType from '../Settings/SelectStaffUserType'
import StaffPermissionsList from '../Settings/StaffPermissionsList'
import {enableGroupUser, createGroupUser, disableGroupUser, resendStaffInvite, updateGroupUser} from '../../lib/queries'
import {useSelector} from 'react-redux'
import {useDispatch} from 'react-redux'

import {updateOrganizationOwnerIdsInState} from '../../actions/globalAdminActions'
import MultiBuildingSelector from './MultiBuildingSelector'
import {selectBuildingsByActiveOrganization} from '../../reducers/selectors'
import {v4 as uuid} from 'uuid'
import DiscardChangesDialog from '../DiscardChangesDialog'
import {spaceMedium} from '../../assets/styles/spacing'
import {isGlobalAdminUserType} from '../../Utils/globalAdminUtils'

const EMPTY_STATE = {
  id: '',
  staffName: '',
  email: '',
  userType: PERMISSION_GROUP_NOT_RESTRICTED,
  buildingIds: [],
  isDisabled: null,
}

function CreateOrEditUser(props) {
  const dispatch = useDispatch()
  const organization = useSelector(state => state.GlobalAdmin.organizationsLookup[state.GlobalAdmin.activeOrganizationId])
  const classes = useStyles()
  const isCreateMode = !props.staffId
  const isEditingASuperAdminOrFormerUser = !isCreateMode && (props.userType === PERMISSION_GROUP_SUPER_ADMIN || props.isDisabled)
  const isNotEditable = isEditingASuperAdminOrFormerUser || [PERMISSION_GROUP_SUPER_ADMIN, PERMISSION_GROUP_VENDOR].includes(props.userType)
  const staffLookup = useSelector(state => state.GlobalAdmin.staffLookup)
  const staffByBuilding = useSelector(state => state.GlobalAdmin.staffByBuilding)
  const buildingsInOrg = useSelector(selectBuildingsByActiveOrganization)
  const activeOrganizationId = useSelector(state => state.GlobalAdmin.activeOrganizationId)
  const authedStaffModel = useSelector(state => state.Staff.authedStaffModel) || {}
  const updateOrganizationOwnerIdsWithRedux = async (staffId, makeOwner) => {
    await updateOrganizationOwnerIdsInState(dispatch, organization, staffId, makeOwner)
  }
  // Determine if editing self as global admin
  const isEditingSelfGlobalAdmin =
    !!authedStaffModel.id && !isCreateMode && authedStaffModel.id === props.staffId && isGlobalAdminUserType(organization, authedStaffModel)
  const [isSaving, setIsSaving] = useState(false)
  const [isSendingInvite, setIsSendingInvite] = useState(false)

  // initialize our form controls
  const {invalidItems, formChanged, setForm, form, validateForm, resetInitialForm} = useFormChanged(
    EMPTY_STATE,
    useFormChanged.PropLevelValidation({
      staffName: NonEmptyStringValidator,
      email: EmailValidator,
      userType: NonEmptyValidator,
      buildingIds: NonEmptyArrayValidator,
    }),
  )

  // load our form based on the documentId prop
  useEffect(() => {
    if (props.staffId) {
      const selectedUser = staffLookup[props.staffId]
      if (!selectedUser) {
        console.warn(`Staff user with ID ${props.staffId} not found in staffLookup.`)
        return
      }

      // Determine userType for dashboard display
      let userType = selectedUser.permissionGroup
      if (isGlobalAdminUserType(organization, selectedUser)) {
        userType = PERMISSION_GROUP_GLOBAL_ADMIN
      }

      const nextForm = {
        id: selectedUser.id,
        staffName: selectedUser.displayName,
        email: selectedUser.userName,
        userType,
      }
      nextForm.buildingIds = buildingsInOrg
        .filter(b => staffByBuilding[b.id] && staffByBuilding[b.id].some(dId => dId === selectedUser.id))
        .map(b => b.id)

      resetInitialForm(nextForm)
    } else {
      resetInitialForm(EMPTY_STATE)
    }
  }, [props.staffId, staffLookup, organization])

  const emailTextInput = (
    <TextInput
      label="Email Address"
      placeholder=""
      value={form.email}
      maxLength={120}
      onChange={val => setForm({email: val})}
      isRequired={true}
      error={invalidItems.includes('email')}
      isDisabled={isNotEditable}
    />
  )

  const handleResendInvite = () => {
    setIsSendingInvite(true)
    resendStaffInvite(form.email, form.staffName, form.buildingIds[0])
      .then(() => {
        window.alert('Invite email has been sent successfully')
      })
      .catch(err => {
        window.alert('Failed to send invite email: ' + err.message)
      })
      .finally(() => {
        setIsSendingInvite(false)
      })
  }

  const handleSave = async () => {
    if (!validateForm()) {
      return
    }

    setIsSaving(true)
    let buildingsToRemove = []
    let buildingsToAdd = [...form.buildingIds]
    let staffId = form.id

    const groupNameToSave = isEditingSelfGlobalAdmin
      ? undefined // cannot edit your own status
      : form.userType === PERMISSION_GROUP_GLOBAL_ADMIN
      ? PERMISSION_GROUP_NOT_RESTRICTED
      : form.userType

    const inputObj = {
      displayName: form.staffName.trim(),
      userName: form.email.trim(),
      groupName: groupNameToSave,
    }
    if (form.id) {
      inputObj.staffId = form.id
      await updateGroupUser(inputObj)

      const currentListOfBuildings = Object.entries(staffByBuilding)
        .filter(([, staffIds]) => staffIds.includes(form.id))
        .map(([bId]) => bId)
      buildingsToRemove = currentListOfBuildings.filter(bId => !form.buildingIds.includes(bId))
    } else {
      inputObj.buildingId = [form.buildingIds[0]] // only use 1 building for creation
      inputObj.password = uuid().substring(0, 16)

      const res = await createGroupUser(inputObj)
      staffId = res.id
      buildingsToAdd = form.buildingIds.slice(1) // remove the first building since it's already assigned
    }

    // add to new building list if needed
    if (buildingsToAdd.length > 0) {
      for (const bId of buildingsToAdd) {
        await enableGroupUser({staffId: staffId, buildingId: bId})
      }
    }

    // remove from old building list if needed
    if (buildingsToRemove.length > 0) {
      for (const bId of buildingsToRemove) {
        await disableGroupUser({staffId: staffId, buildingId: bId})
      }
    }

    // Update Organization.ownerIds if needed
    if (activeOrganizationId && staffId) {
      await updateOrganizationOwnerIdsWithRedux(staffId, form.userType === PERMISSION_GROUP_GLOBAL_ADMIN)
    }
    setIsSaving(false)
    resetInitialForm(form, () => {
      if (typeof props.onSave === 'function') {
        props.onSave()
      }
    })
  }

  const handleDeactivate = async () => {
    setIsSaving(true)
    const currentListOfBuildings = Object.entries(staffByBuilding)
      .filter(([, staffIds]) => staffIds.includes(form.id))
      .map(([bId]) => bId)

    for (const bId of currentListOfBuildings) {
      await disableGroupUser({staffId: form.id, buildingId: bId})
    }
    // Remove from Organization.ownerIds if present
    if (activeOrganizationId && form.id) {
      await updateOrganizationOwnerIdsWithRedux({
        staffId: form.id,
        makeOwner: false,
      })
    }
    setIsSaving(false)

    resetInitialForm({...form, buildingIds: []}, () => {
      if (typeof props.onDeactivate === 'function') {
        props.onDeactivate()
      }
    })
  }

  return (
    <div className={classes.container}>
      <Grid container spacing={3}>
        {isCreateMode && (
          <Grid item xs={12}>
            <Typography style={{color: Colors.rxrRedColor}}>{Constant.ALL_FIELDS_REQUIRED}</Typography>
          </Grid>
        )}
        <Grid item xs={12}>
          <TextInput
            label="Staff name"
            placeholder=""
            maxLength={120}
            onChange={val => setForm({staffName: val})}
            value={form.staffName}
            isRequired={true}
            isDisabled={isNotEditable}
            error={invalidItems.includes('staffName')}
          />
        </Grid>

        {!isCreateMode ? (
          <React.Fragment>
            <Grid item xs={9}>
              {emailTextInput}
            </Grid>
            <Grid item xs={3}>
              <RXRButton
                isLoading={isSendingInvite}
                className={classes.inviteButton}
                type={RXRButton.TYPE_SECONDARY}
                onClick={handleResendInvite}
                disabled={isNotEditable}
              >
                {'Resend invite'}
              </RXRButton>
            </Grid>
          </React.Fragment>
        ) : (
          <Grid item xs={12}>
            {emailTextInput}
          </Grid>
        )}
        <Grid item xs={6}>
          <SelectStaffUserType
            isNewUser={isCreateMode}
            value={form.userType}
            onChange={val => {
              if (val === PERMISSION_GROUP_GLOBAL_ADMIN) {
                setForm({
                  userType: val,
                  buildingIds: buildingsInOrg.map(b => b.id),
                })
              } else {
                setForm({userType: val})
              }
            }}
            error={invalidItems.includes('userType')}
            isDisabled={isNotEditable}
            isEditingSelfGlobalAdmin={isEditingSelfGlobalAdmin}
            helperText={(isEditingSelfGlobalAdmin && Constant.DESCRIPTION_GLOBAL_ADMIN_DASHBOARD_USER_TYPE) || ''}
          />
        </Grid>

        <Grid item xs={12}>
          <div className={[classes.buildingsTitle, invalidItems.includes('buildingIds') ? classes.errorText : ''].join(' ')}>
            Properties (at least 1 required)
          </div>
          <MultiBuildingSelector
            value={form.buildingIds}
            onChange={v => setForm({buildingIds: v})}
            className={buildingsInOrg.length > 5 ? classes.twoColumnLayout : undefined}
          />
        </Grid>
      </Grid>

      <StaffPermissionsList userType={form.userType} />
      <div style={{flex: 1}}></div>
      <div style={{marginTop: spaceMedium, flexShrink: 0}}>
        <Grid container spacing={3}>
          <Grid item xs={4}>
            <RXRButton onClick={handleSave} isLoading={isSaving} disabled={isEditingASuperAdminOrFormerUser || !formChanged}>
              Save
            </RXRButton>
          </Grid>

          <Grid item xs={4} align="center">
            <RXRButton onClick={props.onCancel} isLoading={isSaving} type={RXRButton.TYPE_TEXT}>
              Cancel
            </RXRButton>
          </Grid>

          {!isCreateMode && (
            <Grid item xs={4} align="right">
              <RXRButton
                onClick={handleDeactivate}
                isLoading={isSaving}
                disabled={isEditingASuperAdminOrFormerUser}
                type={RXRButton.TYPE_DESTRUCTIVE}
              >
                Deactivate
              </RXRButton>
            </Grid>
          )}
        </Grid>
      </div>

      <DiscardChangesDialog hasChanges={formChanged} />
    </div>
  )
}

const useStyles = makeStyles(theme => ({
  container: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },

  buildingsTitle: {
    fontSize: TypographyStyle.fontSizeSmall,
    color: theme.palette.primary.main,
  },

  errorText: {
    color: Colors.rxrRedColor,
  },

  twoColumnLayout: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
  },

  inviteButton: {
    height: `55px !important`,
    width: '100%',
  },
}))

CreateOrEditUser.propTypes = {
  onCancel: PropTypes.func,
  onSave: PropTypes.func,
  onDeactivate: PropTypes.func,
  staffId: PropTypes.string,
}

export default CreateOrEditUser
